import React, {
  useEffect,
  useState,
} from "react";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  useDispatch,
} from "react-redux";

import {
  useTranslation,
} from "react-i18next";

import {
  FiMinus,
  FiPlus,
  FiX,
  FiShoppingBag,
} from "react-icons/fi";

import {
  addToOrder,
} from "../../redux/actions/order";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  media,
} from "../../server";


function AddToOrderModal({
  isOpen,
  onClose,
  meal,
  onAdded,
}) {
  const dispatch =
    useDispatch();

  const { t } =
    useTranslation();

  const {
    currentDir,
  } = useStateContext();


  const [
    quantity,
    setQuantity,
  ] = useState(1);

  const [
    notes,
    setNotes,
  ] = useState("");


  /* =========================
     RESET
  ========================== */

  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setNotes("");
    }
  }, [isOpen]);


  /* =========================
     LOCK PAGE SCROLL
  ========================== */

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow =
        "hidden";
    } else {
      document.body.style.overflow =
        "";
    }

    return () => {
      document.body.style.overflow =
        "";
    };
  }, [isOpen]);


  /* =========================
     DATA
  ========================== */

  const title =
    currentDir === "ltr"
      ? meal?.titleEn
      : meal?.titleAr;


  const price =
    Number(
      meal?.price || 0
    );


  const total =
    price * quantity;


  /* =========================
     QUANTITY
  ========================== */

  const increase = () => {
    setQuantity(
      (prev) =>
        Math.min(
          prev + 1,
          99
        )
    );
  };


  const decrease = () => {
    setQuantity(
      (prev) =>
        Math.max(
          prev - 1,
          1
        )
    );
  };


  /* =========================
     ADD TO ORDER
  ========================== */

  const handleAdd = () => {
    if (!meal?._id) {
      return;
    }

    const lineId =
      `${
        meal._id
      }-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 7)}`;


    dispatch(
      addToOrder({
        lineId,

        mealId:
          meal._id,

        titleAr:
          meal?.titleAr,

        titleEn:
          meal?.titleEn,

        Image:
          meal?.Image,

        price,

        quantity,

        notes:
          notes.trim(),
      })
    );


    onClose();


    if (onAdded) {
      onAdded();
    }
  };


  return (
    <AnimatePresence>
      {isOpen && (

        /*
         * هذا هو الجزء المهم.
         *
         * Wrapper يغطي الشاشة بالكامل
         * ويقوم بتمركز النافذة بواسطة Flex.
         *
         * لا يوجد:
         * left-1/2
         * translate-x
         * translate-y
         */

        <div
          className="
            fixed
            inset-0

            z-[1200]

            flex
            items-center
            justify-center

            p-4
            md:p-6
          "
        >

          {/* =========================
              OVERLAY
          ========================== */}

          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
            }}
            onClick={
              onClose
            }
            className="
              absolute
              inset-0

              bg-color-2/60

              backdrop-blur-sm
            "
          />


          {/* =========================
              MODAL
          ========================== */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.94,
              y: 25,
            }}

            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}

            exit={{
              opacity: 0,
              scale: 0.95,
              y: 20,
            }}

            transition={{
              duration: 0.25,
              ease: "easeOut",
            }}

            className="
              relative
              z-10

              w-full
              max-w-[520px]

              max-h-[calc(100vh-2rem)]

              overflow-y-auto

              rounded-[1.75rem]
              md:rounded-[2rem]

              border
              border-color-1/20

              bg-back-primery
              dark:bg-back-darksecondary

              shadow-[0_30px_90px_rgba(68,37,20,0.35)]
            "
          >

            {/* =========================
                IMAGE
            ========================== */}

            <div
              className="
                relative

                h-[170px]
                sm:h-[190px]

                overflow-hidden
              "
            >
              <img
                src={`${media}${meal?.Image}`}
                alt={title}
                className="
                  w-full
                  h-full

                  object-cover
                "
              />


              {/* Overlay */}

              <div
                className="
                  absolute
                  inset-0

                  bg-gradient-to-t

                  from-color-2/80
                  via-color-2/10
                  to-transparent
                "
              />


              {/* Close */}

              <button
                type="button"
                onClick={
                  onClose
                }
                aria-label={t(
                  "close"
                )}
                className="
                  absolute

                  top-4
                  end-4

                  flex

                  w-10
                  h-10

                  items-center
                  justify-center

                  rounded-full

                  border
                  border-white/15

                  bg-black/30

                  backdrop-blur-md

                  text-white

                  transition-all
                  duration-300

                  hover:bg-color-1
                "
              >
                <FiX
                  size={19}
                />
              </button>


              {/* Meal Title */}

              <div
                className="
                  absolute

                  bottom-5
                  start-5
                  end-5
                "
              >
                <span
                  className="
                    block

                    font-kufi
                    text-[10px]

                    text-color-1

                    mb-1
                  "
                >
                  {t(
                    "add to order"
                  )}
                </span>


                <h2
                  className="
                    font-kufi
                    font-bold

                    text-xl
                    md:text-2xl

                    text-white
                  "
                >
                  {title}
                </h2>
              </div>

            </div>


            {/* =========================
                CONTENT
            ========================== */}

            <div
              className="
                p-5
                md:p-7
              "
            >

              {/* =========================
                  QUANTITY
              ========================== */}

              <div
                className="
                  flex
                  items-center
                  justify-between

                  gap-4

                  mb-7
                "
              >

                <div>
                  <h3
                    className="
                      font-kufi
                      font-semibold

                      text-sm
                      md:text-base

                      text-tcolor-4
                      dark:text-white
                    "
                  >
                    {t(
                      "order quantity"
                    )}
                  </h3>

                  <p
                    className="
                      font-kufi
                      text-[10px]
                      md:text-xs

                      text-n-5
                      dark:text-tcolor-3

                      mt-1
                    "
                  >
                    {t(
                      "choose desired quantity"
                    )}
                  </p>
                </div>


                {/* Counter */}

                <div
                  className="
                    flex
                    items-center

                    shrink-0

                    rounded-full

                    border
                    border-color-2/10
                    dark:border-white/10

                    bg-back-secondary
                    dark:bg-back-darkprimery

                    p-1
                  "
                >

                  <button
                    type="button"
                    onClick={
                      decrease
                    }
                    disabled={
                      quantity <= 1
                    }
                    className="
                      flex

                      w-10
                      h-10

                      items-center
                      justify-center

                      rounded-full

                      text-color-1

                      transition-all
                      duration-300

                      hover:bg-color-1
                      hover:text-white

                      disabled:opacity-30
                      disabled:hover:bg-transparent
                      disabled:hover:text-color-1
                    "
                  >
                    <FiMinus />
                  </button>


                  <span
                    className="
                      min-w-[42px]

                      text-center

                      font-kufi
                      font-bold

                      text-tcolor-4
                      dark:text-white
                    "
                  >
                    {quantity}
                  </span>


                  <button
                    type="button"
                    onClick={
                      increase
                    }
                    className="
                      flex

                      w-10
                      h-10

                      items-center
                      justify-center

                      rounded-full

                      text-color-1

                      transition-all
                      duration-300

                      hover:bg-color-1
                      hover:text-white
                    "
                  >
                    <FiPlus />
                  </button>

                </div>

              </div>


              {/* =========================
                  NOTES
              ========================== */}

              <div
                className="
                  mb-6
                "
              >
                <label
                  htmlFor="order-notes"
                  className="
                    block

                    font-kufi
                    font-semibold

                    text-sm

                    text-tcolor-4
                    dark:text-white

                    mb-2
                  "
                >
                  {t(
                    "order notes"
                  )}
                </label>


                <textarea
                  id="order-notes"

                  value={
                    notes
                  }

                  onChange={(
                    event
                  ) =>
                    setNotes(
                      event.target
                        .value
                    )
                  }

                  maxLength={
                    300
                  }

                  rows={4}

                  placeholder={t(
                    "order notes placeholder"
                  )}

                  className="
                    w-full

                    resize-none

                    rounded-[1.25rem]

                    border
                    border-color-2/10
                    dark:border-white/10

                    bg-back-secondary
                    dark:bg-back-darkprimery

                    p-4

                    font-kufi
                    text-base

                    text-tcolor-4
                    dark:text-white

                    placeholder:text-n-4

                    outline-none

                    transition-all
                    duration-300

                    focus:border-color-1
                    focus:ring-2
                    focus:ring-color-1/10
                  "
                />


                <div
                  className="
                    flex
                    justify-end

                    mt-1.5
                  "
                >
                  <span
                    className="
                      text-[10px]

                      text-n-5
                      dark:text-tcolor-3
                    "
                  >
                    {notes.length}
                    /300
                  </span>
                </div>

              </div>


              {/* =========================
                  TOTAL
              ========================== */}

              <div
                className="
                  flex
                  items-end
                  justify-between

                  gap-4

                  py-5

                  border-y
                  border-color-2/10
                  dark:border-white/10

                  mb-6
                "
              >
                <div>
                  <span
                    className="
                      block

                      font-kufi
                      text-xs

                      text-n-5
                      dark:text-tcolor-3

                      mb-1
                    "
                  >
                    {t(
                      "subtotal"
                    )}
                  </span>

                  <span
                    className="
                      font-kufi
                      text-[10px]

                      text-n-4
                    "
                  >
                    {quantity}{" "}
                    ×{" "}
                    {price}{" "}
                    {t(
                      "currency"
                    )}
                  </span>
                </div>


                <div
                  className="
                    flex
                    items-end

                    gap-1.5
                  "
                >
                  <span
                    className="
                      text-3xl
                      md:text-4xl

                      font-bold

                      text-color-1
                    "
                  >
                    {total}
                  </span>

                  <span
                    className="
                      font-kufi
                      text-xs

                      text-color-1

                      mb-1
                    "
                  >
                    {t(
                      "currency"
                    )}
                  </span>
                </div>

              </div>


              {/* =========================
                  ADD BUTTON
              ========================== */}

              <motion.button
                type="button"

                whileHover={{
                  y: -2,
                }}

                whileTap={{
                  scale: 0.98,
                }}

                onClick={
                  handleAdd
                }

                className="
                  flex

                  w-full

                  min-h-[56px]

                  items-center
                  justify-center

                  gap-3

                  rounded-full

                  bg-color-1

                  px-6
                  py-4

                  font-kufi
                  font-semibold

                  text-white

                  shadow-[0_12px_35px_rgba(165,119,58,0.22)]

                  transition-colors
                  duration-300

                  hover:bg-color-2
                "
              >
                <FiShoppingBag
                  size={18}
                />

                {t(
                  "add to order"
                )}

                <span
                  className="
                    opacity-60
                  "
                >
                  •
                </span>

                <span>
                  {total}{" "}
                  {t(
                    "currency"
                  )}
                </span>
              </motion.button>

            </div>

          </motion.div>

        </div>

      )}
    </AnimatePresence>
  );
}


export default AddToOrderModal;